import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';
import { Loader2 } from 'lucide-react';
import { cx } from '@/lib/cx';

export type ButtonVariant = 'gold' | 'primary' | 'secondary' | 'ghost' | 'danger' | 'link';
export type ButtonSize = 'sm' | 'md' | 'lg';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  loading?: boolean;
  icon?: ReactNode;
  iconRight?: ReactNode;
  block?: boolean;
}

const variants: Record<ButtonVariant, string> = {
  // The single most important next action on a screen.
  gold:
    'gold-gradient text-ink-900 shadow-gold border border-gold-600/40 hover:brightness-[1.04] sheen-host font-semibold',
  // "Black marble" with a gold edge.
  primary:
    'bg-ink-900 text-white border border-ink-900 shadow-e2 hover:bg-ink-700 sheen-host [box-shadow:inset_0_0_0_1px_rgb(208_162_74/0.35),var(--shadow-e2)]',
  secondary: 'bg-surface-0 text-ink-900 border border-line-strong shadow-e1 hover:border-gold-300 hover:bg-gold-50/40',
  ghost: 'bg-transparent text-ink-700 border border-transparent hover:bg-surface-2 hover:text-ink-900',
  danger: 'bg-surface-0 text-danger-600 border border-danger-600/30 hover:bg-danger-50',
  link: 'bg-transparent text-gold-700 border border-transparent underline-offset-4 hover:underline px-0!',
};

const sizes: Record<ButtonSize, string> = {
  sm: 'h-8 px-3 text-[13px] gap-1.5 rounded-[8px]',
  md: 'h-10 px-4 text-sm gap-2 rounded-(--radius-button)',
  lg: 'h-12 px-6 text-[15px] gap-2.5 rounded-(--radius-button)',
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = 'secondary', size = 'md', loading, icon, iconRight, block, className, children, disabled, type, ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type ?? 'button'}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      className={cx(
        'relative inline-flex items-center justify-center whitespace-nowrap font-medium select-none',
        'transition-[transform,background-color,border-color,box-shadow,filter,color] duration-150 ease-(--ease-standard)',
        'active:scale-[0.975] disabled:opacity-50 disabled:active:scale-100',
        variants[variant],
        sizes[size],
        block && 'w-full',
        className,
      )}
      {...rest}
    >
      {loading ? <Loader2 className="size-4 animate-spin" aria-hidden /> : icon}
      {children && <span className="relative z-[2]">{children}</span>}
      {iconRight}
    </button>
  );
});

export const IconButton = forwardRef<
  HTMLButtonElement,
  ButtonHTMLAttributes<HTMLButtonElement> & { label: string; size?: 'sm' | 'md'; tone?: 'default' | 'danger' }
>(function IconButton({ label, size = 'md', tone = 'default', className, children, ...rest }, ref) {
  return (
    <button
      ref={ref}
      type="button"
      aria-label={label}
      title={label}
      className={cx(
        'inline-flex shrink-0 items-center justify-center rounded-[9px] text-ink-500 transition-[background-color,color,transform] duration-150',
        'hover:bg-surface-2 hover:text-ink-900 active:scale-95 disabled:opacity-40',
        tone === 'danger' && 'hover:bg-danger-50 hover:text-danger-600',
        size === 'sm' ? 'size-7' : 'size-9',
        className,
      )}
      {...rest}
    >
      {children}
    </button>
  );
});
